import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatTimestamp = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' });
  } catch (_e) {
    return iso;
  }
};

const renderDiff = (diff) => {
  if (diff.kind === 'module') {
    return (
      <span>
        <span className="font-medium">{diff.label}</span>: <span className="text-gray-500">{diff.from}</span>
        {' → '}
        <span className="text-emerald-700 dark:text-emerald-400 font-medium">{diff.to}</span>
      </span>
    );
  }
  if (diff.kind === 'role') {
    return (
      <span>
        <span className="font-medium">Role</span>: <span className="text-gray-500">{diff.from || 'none'}</span>
        {' → '}
        <span className="text-emerald-700 dark:text-emerald-400 font-medium">{diff.to || 'none'}</span>
      </span>
    );
  }
  if (diff.kind === 'athletes') {
    return (
      <span>
        <span className="font-medium">Athletes</span>: {diff.from_count} → {diff.to_count}
        {diff.added?.length ? <span className="text-emerald-600 ml-1">+{diff.added.length}</span> : null}
        {diff.removed?.length ? <span className="text-red-600 ml-1">-{diff.removed.length}</span> : null}
      </span>
    );
  }
  return null;
};

const ChangeFeedTab = ({ refreshTick, onReverted }) => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [revertingId, setRevertingId] = useState(null);

  const fetchEntries = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/permissions/change-feed?page=1&page_size=50`);
      setEntries(res.data.entries || []);
    } catch (_e) {
      toast.error('Failed to load change feed');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchEntries();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTick]);

  const handleRevert = async (id) => {
    if (!window.confirm('Revert this access change? The user will be signed out immediately.')) return;
    setRevertingId(id);
    try {
      await axios.post(`${API}/permissions/change-feed/${id}/revert`);
      toast.success('Reverted. Affected user session invalidated.');
      await fetchEntries();
      onReverted?.();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to revert');
    } finally {
      setRevertingId(null);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-emerald-600" />
      </div>
    );
  }

  return (
    <Card data-testid="change-feed-card">
      <CardHeader>
        <CardTitle>Recent Access Changes</CardTitle>
        <CardDescription>Audit trail of access-control changes. Revert any entry to restore the previous state.</CardDescription>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <p className="text-center py-8 text-gray-500">No access changes recorded yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Admin</TableHead>
                  <TableHead>Target User</TableHead>
                  <TableHead>Change Summary</TableHead>
                  <TableHead>Timestamp</TableHead>
                  <TableHead className="text-right">Action</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.map((entry) => (
                  <TableRow key={entry.id} data-testid={`change-entry-${entry.id}`}>
                    <TableCell className="align-top text-sm">{entry.admin_email || 'system'}</TableCell>
                    <TableCell className="align-top text-sm">
                      <div className="font-medium">{entry.target_full_name || entry.target_email}</div>
                      <div className="text-xs text-gray-500">{entry.target_role}</div>
                    </TableCell>
                    <TableCell className="align-top text-xs">
                      <div className="space-y-1 max-w-md">
                        {(entry.diffs || []).slice(0, 5).map((d, idx) => (
                          <div key={idx}>{renderDiff(d)}</div>
                        ))}
                        {(entry.diffs?.length || 0) > 5 && (
                          <div className="text-gray-400">+{entry.diffs.length - 5} more</div>
                        )}
                        <div className="pt-1">
                          <Badge variant="outline" className="text-[10px]">
                            {entry.change_type}
                          </Badge>
                          {entry.reverted && (
                            <Badge className="bg-amber-100 text-amber-800 ml-1 text-[10px]">Reverted</Badge>
                          )}
                        </div>
                      </div>
                    </TableCell>
                    <TableCell className="align-top text-xs text-gray-500 whitespace-nowrap">
                      {formatTimestamp(entry.timestamp)}
                    </TableCell>
                    <TableCell className="align-top text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        disabled={entry.reverted || revertingId === entry.id}
                        onClick={() => handleRevert(entry.id)}
                        data-testid={`revert-${entry.id}`}
                      >
                        {revertingId === entry.id ? 'Reverting...' : entry.reverted ? 'Reverted' : 'Revert'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};

export default ChangeFeedTab;
